/**
 * V118-0-4 / ADR-0117 §1: the obligation set — one extraction of one stored statute text, from which every law
 * artefact (terms, controls, rule, fixtures, spec, delta) is derived.
 *
 * Two shapes live here:
 *   - `RawExtraction` (zod): what the model must return. Strict about types, enums and required fields; arrays the
 *     model leaves out default to empty. Quotes only: the model never returns offsets (ADR-0117 §2).
 *   - `ObligationSet` / `ObligationRecord`: what code builds from it — quotes located to raw offsets, ids assigned,
 *     control derived, `missing_source` listing every span code could not locate.
 */
import { z } from 'zod';
import { ACTOR_CLASSES, BINDS_WHOM_SCOPES, DEONTIC_KINDS } from '../../data/intake-enums.js';
/** Bump when the record shape changes; part of the extractor version (ADR-0117 §4). */
export const OBLIGATION_SET_SCHEMA_VERSION = 3;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// v2 (2026-11-01 eval): a short span may come back without a quote; code then lists it in missing_source instead of
// failing the whole law. The operative `excerpt` stays required.
const nullishQuote = z.preprocess((v) => (typeof v === 'string' && v.trim() ? v : null), z.string().min(1).nullable());
const quoted = z.object({ text: z.string().min(1), quote: nullishQuote });
const threshold = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('review_every_days'), value: z.number().positive() }),
    z.object({ kind: z.literal('review_every_hours'), value: z.number().positive() }),
    z.object({ kind: z.literal('metric_min'), metric: z.string().min(1), value: z.number() }),
    z.object({ kind: z.literal('metric_max'), metric: z.string().min(1), value: z.number() }),
]);
const dated = z.object({ date: z.preprocess((v) => (typeof v === 'string' && ISO_DATE.test(v) ? v : null), z.string().regex(ISO_DATE).nullable()), quote: nullishQuote });
/** "null", "" and null all mean no value (gpt-4o writes the string "null"). */
const nullishString = (inner) => z.preprocess((v) => (v === null || v === undefined || v === '' || v === 'null' ? null : v), inner.nullable());
export const RawObligationSchema = z.object({
    section: nullishString(z.string().min(1)),
    excerpt: z.string().min(1),
    deontic: z.enum(DEONTIC_KINDS),
    binds_whom: z.object({
        scope: z.enum(BINDS_WHOM_SCOPES),
        // v2: an actor outside the list reads as `other` (the stated fallback), never a failed law.
        actor_class: z.preprocess((v) => (ACTOR_CLASSES.includes(v) ? v : 'other'), z.enum(ACTOR_CLASSES)),
        exclusions: z.array(quoted).default([]),
    }),
    obligation: z.string().min(1),
    trigger_conditions: z.array(quoted).default([]),
    unless: z.array(quoted).default([]),
    thresholds: z.array(threshold).default([]),
    deadlines: z.array(dated.extend({ text: z.string().min(1) })).default([]),
    effective_date: z.preprocess((v) => (v && typeof v === 'object' && !v.date && !v.quote ? null : v), dated.nullable()).default(null),
    duty: nullishString(z.string().regex(/^[a-z][a-z0-9_]*$/)).default(null),
    new_control: z.object({ title: z.string().min(1), definition: z.string().min(1) }).nullable().default(null),
    fixtures: z.array(z.object({
        description: z.string().min(1),
        answers: z.record(z.string(), z.unknown()),
        expect_applies: z.boolean(),
    })).default([]),
    confidence: z.number().min(0).max(1),
});
export const RawExtractionSchema = z.object({
    // A JSON-encoded array (as gpt-4o has returned for `duties`) is read as the array.
    obligations: z.preprocess((v) => { if (typeof v !== 'string')
        return v; try {
        return JSON.parse(v);
    }
    catch {
        return v;
    } }, z.array(RawObligationSchema)),
    applicability: z.array(quoted).default([]),
    missing_concepts: z.array(z.string()).default([]),
    cross_references: z.array(z.string()).default([]),
});
/** Raw validation errors, one line each, as the retry prompt shows them (Instructor-style). */
export function extractionErrors(output) {
    const r = RawExtractionSchema.safeParse(output);
    if (r.success)
        return [];
    return r.error.issues.slice(0, 40).map((i) => `${i.path.length ? i.path.join('.') : '(root)'}: ${i.message}`);
}
//# sourceMappingURL=obligation-set.js.map